/**
 * Win cascade for the classic 2D renderer. Mirrors the bouncing-cards
 * effect from the original vanilla Klondike (assets/classic/index.js):
 * each foundation card flies out from its slot with random horizontal
 * velocity and slight upward kick, gravity pulls it down, it bounces off
 * the felt's bottom edge with energy loss, then exits screen-left or
 * screen-right.
 *
 * DOM-based (not canvas): we move the existing card divs themselves so
 * the spritesheet faces stay crisp at any pixel ratio.
 */
export interface WinCascadeOpts {
  /** felt root - cards animate inside its bounds */
  root: HTMLElement;
  /** cards to throw, in the order they should leave their pile */
  cards: HTMLElement[];
  /** ms between successive throws (default 200) */
  throwIntervalMs?: number;
  /** called once all thrown cards exit screen */
  onComplete?: () => void;
}

export interface WinCascade {
  /** stop the animation and leave any active cards where they are */
  cancel(): void;
  /** true while the cascade is still throwing or animating particles */
  isActive(): boolean;
}

interface Particle {
  el: HTMLElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export function runWinCascade(opts: WinCascadeOpts): WinCascade {
  const { root, cards } = opts;
  const interval = opts.throwIntervalMs ?? 200;
  const bounds = root.getBoundingClientRect();
  const floorY = bounds.height;
  const CARD_W = 71;
  const CARD_H = 96;
  const GRAVITY = 0.98;
  const RESTITUTION = 0.85;

  const particles: Particle[] = [];
  let throwIdx = 0;
  let lastThrow = 0;
  let raf = 0;
  let cancelled = false;
  let started = false;

  function throwNext(): boolean {
    if (throwIdx >= cards.length) return false;
    const el = cards[throwIdx]!;
    throwIdx++;
    const r = el.getBoundingClientRect();
    const x = r.left - bounds.left;
    const y = r.top - bounds.top;
    let vx = (Math.floor(Math.random() * 6) - 3) * 2;
    if (vx === 0) vx = 2;
    const vy = -Math.random() * 16;
    // Detach from the layout: kill the CSS transition so each frame's
    // left/top write is instant, and lift the z-index above HUD bar.
    el.style.transition = 'none';
    el.style.zIndex = '500';
    particles.push({ el, x, y, vx, vy });
    return true;
  }

  function step(now: number) {
    if (cancelled) return;
    if (!started) {
      started = true;
      lastThrow = now - interval;
    }
    if (now - lastThrow >= interval && throwIdx < cards.length) {
      throwNext();
      lastThrow = now;
    }
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]!;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += GRAVITY;
      if (p.y > floorY - CARD_H) {
        p.y = floorY - CARD_H;
        p.vy = -p.vy * RESTITUTION;
      }
      if (p.x < -CARD_W || p.x > bounds.width + CARD_W) {
        particles.splice(i, 1);
        continue;
      }
      p.el.style.left = `${Math.floor(p.x)}px`;
      p.el.style.top = `${Math.floor(p.y)}px`;
    }
    if (throwIdx >= cards.length && particles.length === 0) {
      opts.onComplete?.();
      return;
    }
    raf = requestAnimationFrame(step);
  }
  raf = requestAnimationFrame(step);

  return {
    cancel() {
      if (cancelled) return;
      cancelled = true;
      cancelAnimationFrame(raf);
    },
    isActive() {
      return !cancelled && (throwIdx < cards.length || particles.length > 0);
    },
  };
}
